import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Grid, Box, Typography, alpha, IconButton
} from '@mui/material';
import { IonIcon } from '@ionic/react';
import { close, save, medkit } from 'ionicons/icons';
import * as ProcedureService from '../../../api/procedure.service';
import { Procedure } from '../../../models/procedure';

interface ProcedureModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    procedure?: Procedure | null; // Pass the procedure data directly
}

const ProcedureModal: React.FC<ProcedureModalProps> = ({ open, onClose, onSuccess, procedure }) => {
    const isEdit = Boolean(procedure);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<any>({
        procedure_code: '',
        name: '',
        description: '',
        base_fee: '0.00'
    });

    useEffect(() => {
        if (open && procedure) {
            // Use the passed procedure data for editing
            setFormData({
                name: procedure.name,
                description: procedure.description,
                base_fee: procedure.base_fee,
                geacloud_procedures_id: procedure.geacloud_procedures_id
            });
        } else if (open && !procedure) {
            // Reset form for new procedure
            setFormData({
                procedure_code: '',
                name: '',
                description: '',
                base_fee: '0.00'
            });
        }
    }, [open, procedure]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEdit && procedure) {
                // For update, include geacloud_procedures_id in the payload
                await ProcedureService.updateProcedure({
                    ...formData,
                    geacloud_procedures_id: procedure.geacloud_procedures_id
                });
            } else {
                await ProcedureService.createProcedure(formData);
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Error saving procedure", error);
            alert('Failed to save procedure. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 102, 204, 0.2)'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                color: 'white',
                p: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IonIcon icon={medkit} style={{ fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {isEdit ? 'Edit Procedure' : 'New Medical Procedure'}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                {isEdit ? 'Update procedure information' : 'Create a new procedure'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                    >
                        <IonIcon icon={close} />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Form Content */}
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
                    <Grid container spacing={3}>
                        {/* Procedure Name */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                                Procedure Name *
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="e.g., Surgical Consultation"
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Base Fee */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                                Base Fee (₦) *
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                name="base_fee"
                                type="number"
                                value={formData.base_fee}
                                onChange={handleChange}
                                placeholder="20000.00"
                                disabled={loading}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: alpha('#16a34a', 0.05),
                                        '&:hover': {
                                            bgcolor: alpha('#16a34a', 0.08)
                                        }
                                    },
                                    '& input': {
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        color: '#16a34a'
                                    }
                                }}
                            />
                        </Grid>

                        {/* Description - Always show */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#334155' }}>
                                Description *
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                multiline
                                rows={4}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Provide a detailed description of the medical procedure..."
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        '&:hover': {
                                            bgcolor: '#f1f5f9'
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                {/* Actions */}
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            color: '#64748b',
                            '&:hover': {
                                bgcolor: '#f1f5f9'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={<IonIcon icon={save} />}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)',
                            boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
                            fontWeight: 600,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0052a3 0%, #003d7a 100%)',
                                boxShadow: '0 6px 16px rgba(0, 102, 204, 0.4)'
                            }
                        }}
                    >
                        {loading ? 'Saving...' : isEdit ? 'Update Procedure' : 'Create Procedure'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ProcedureModal;
